/**
 * localStorage-backed memory of the most recently viewed episodes.
 *
 * Used by:
 * - WelcomeModal → personalized CTA on the final step (uses most recent)
 * - SavedEpisodes empty state → "Save one of your recent views?"
 * - NotesLibrary empty state → "Add a note to a recent episode"
 */
const STORAGE_KEY = "atp:recent-episodes:v2";
const LEGACY_KEY = "atp:recent-episode:v1";
const MAX = 5;

export interface RecentEpisode {
  slug: string;
  title: string;
  podcast_name?: string | null;
  artwork_url?: string | null;
  viewed_at: number;
}

function readList(): RecentEpisode[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.filter(
          (r): r is RecentEpisode =>
            r && typeof r.slug === "string" && typeof r.title === "string"
        );
      }
    }
    // Migrate from legacy single-entry format if present
    const legacy = window.localStorage.getItem(LEGACY_KEY);
    if (legacy) {
      const parsed = JSON.parse(legacy);
      if (parsed && typeof parsed.slug === "string" && typeof parsed.title === "string") {
        return [parsed as RecentEpisode];
      }
    }
    return [];
  } catch {
    return [];
  }
}

function writeList(list: RecentEpisode[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    /* localStorage unavailable — ignore */
  }
}

export function setRecentEpisode(ep: Omit<RecentEpisode, "viewed_at">) {
  const list = readList();
  const filtered = list.filter((r) => r.slug !== ep.slug);
  const next = [{ ...ep, viewed_at: Date.now() }, ...filtered].slice(0, MAX);
  writeList(next);
}

/** Most recent single episode (back-compat for WelcomeModal). */
export function getRecentEpisode(): RecentEpisode | null {
  const list = readList();
  return list[0] ?? null;
}

/** Full list of recently viewed episodes, newest first. */
export function getRecentEpisodes(limit = MAX): RecentEpisode[] {
  return readList().slice(0, limit);
}
